import { useState } from 'react'
import { Send } from 'lucide-react'
import { AttachmentDropzone } from '../../../../components/chat/AttachmentDropzone'
import {
  draftsToPendingAttachments,
  type PendingAttachmentLike,
} from '../../../../hooks/useAttachmentUpload'
import { useSystemIssueAttachmentUpload } from '../../hooks/useSystemIssueAttachmentUpload'
import { filesFromClipboard } from '@/utils/clipboard'

interface IssueMessageEditorProps {
  disabled?: boolean
  disabledReason?: string
  isSending: boolean
  onSend: (content: string, attachments: PendingAttachmentLike[]) => void
}

/**
 * Versão simplificada de `MessageEditor` de Ocorrências: textarea simples
 * (sem `MentionableTextarea` — não existe conceito de @menção em Problemas
 * Reportados) + o mesmo `AttachmentDropzone` compartilhado.
 */
export function IssueMessageEditor({
  disabled,
  disabledReason,
  isSending,
  onSend,
}: IssueMessageEditorProps) {
  const [draft, setDraft] = useState('')
  const {
    drafts,
    addFiles,
    removeDraft,
    reset: resetAttachments,
    isUploading,
    hasErrors,
  } = useSystemIssueAttachmentUpload()

  const handleSend = () => {
    const trimmed = draft.trim()
    const attachments = draftsToPendingAttachments(drafts)
    if ((!trimmed && attachments.length === 0) || isSending || disabled) return
    if (isUploading) return
    onSend(trimmed, attachments)
    setDraft('')
    resetAttachments()
  }

  if (disabled) {
    return (
      <div className="border-t border-border-subtle bg-surface-muted px-4 py-3 text-center text-xs text-muted">
        {disabledReason ?? 'Editor desabilitado.'}
      </div>
    )
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault()
    }
  }
  const handleDrop = (e: React.DragEvent) => {
    const files = Array.from(e.dataTransfer.files ?? [])
    if (files.length === 0) return
    e.preventDefault()
    addFiles(files)
  }

  // Print colado com Ctrl+V vira anexo — mesmo tratamento do formulário de
  // novo problema e do `MessageEditor` de Ocorrências.
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = filesFromClipboard(e.clipboardData)
    if (files.length === 0) return
    e.preventDefault()
    addFiles(files)
  }

  const canSubmit =
    !isSending &&
    !isUploading &&
    !hasErrors &&
    (draft.trim().length > 0 || drafts.some((d) => d.status === 'ready'))

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="border-t border-border-subtle bg-surface px-4 py-3"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onPaste={handlePaste}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
          }
        }}
        rows={2}
        placeholder="Escreva uma mensagem... (arraste ou cole com Ctrl+V para anexar)"
        className="w-full resize-none rounded-[5px] border border-border-subtle bg-surface px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-900/40"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <AttachmentDropzone
            drafts={drafts}
            onAddFiles={addFiles}
            onRemove={removeDraft}
            accept="image/*"
            disabled={isSending}
          />
        </div>
        <button
          type="button"
          onClick={handleSend}
          disabled={!canSubmit}
          className="inline-flex shrink-0 items-center gap-1 rounded-[5px] bg-[#2563eb] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[#1d55d6] disabled:opacity-50"
          title={
            isUploading
              ? 'Aguardando upload de anexos...'
              : hasErrors
                ? 'Remova os anexos com erro antes de enviar.'
                : undefined
          }
        >
          <Send className="h-3.5 w-3.5" />
          {isSending ? 'Enviando…' : isUploading ? 'Aguarde…' : 'Enviar'}
        </button>
      </div>
    </div>
  )
}
